import { CSSProperties, FC, useCallback, useEffect, useRef, useState } from 'react'
import { LAB_CAMERA_FIT, LabCamera, LabMaskBox, labCameraOnBox, loadLabDetectionMask, preloadLabSceneImage } from './labSceneMedia'

import { LAB_PRIVACY_CATEGORY_KEYS, LabPrivacyCategory, LabScene } from './labSdk.types'
import { Nullable } from 'models/helpers'
import styles from './LabRunScenes.module.sass'
import { useTranslation } from 'react-i18next'

/** The Playground's timings (lab-back playground StudioCanvas.vue `sceneEdit`, `sceneGen`, `sceneDetect`, `sceneReveal`) */
export const LAB_SCENE_STRAIGHTEN_MS = 650
export const LAB_SCENE_EDIT_MS = 850
export const LAB_SCENE_GENERATE_MS = 900
export const LAB_DETECTION_ZOOM_MS = 950
export const LAB_DETECTION_HOLD_MS = 1500
export const LAB_DETECTION_FIT_MS = 750

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

interface ShownDetection {
  id: number
  maskUrl: string
  box: LabMaskBox
  categories: LabPrivacyCategory[]
}

interface LabRunScenesProps {
  /** The latest step of the run; each new id is staged once, in order */
  scene: Nullable<LabScene>
  /** The photo under the overlay, until a step brings its own image */
  photoUrl?: string
  /** A scene plays (the halo steps aside meanwhile) */
  onPlayingChange?: (isPlaying: boolean) => void
  /** An edit straightened the photo: the straighten grid plays as its image comes in */
  onStraighten?: () => void
  /** A step's image is on screen: the result's reveal wipes from it, not from one still queued */
  onImageShown?: (url: string) => void
}

/**
 * The Playground's run choreography over the photo, one scene after the other as the order's steps finish:
 * - an edit or a generation crossfades its image in, and it stays on screen: the next steps work on it, and the
 *   straightening crops it, so the detections' masks only line up over it. An edit that straightened plays the grid
 *   first. The image is laid out in its own shape inside the photo's frame.
 * - a detection tints its mask coral, frames it with brackets and a pill naming what was looked for, as the Auto
 *   privacy panel names it ("Photos & frames"; "Faces, Photos & frames" for a blend; in the Studio's type, without the
 *   Playground's coverage), and the camera zooms on it, holds, then moves on.
 * Once the queue is done the camera returns to the whole image: the Playground stays until its reveal, but the
 * Studio's reveal (LabRevealOverlay) wipes the image as it is. A scene already there when this mounts is past.
 */
export const LabRunScenes: FC<LabRunScenesProps> = ({ scene, photoUrl, onPlayingChange, onStraighten, onImageShown }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const fitRef = useRef<HTMLDivElement>(null)
  const layerRef = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState<ShownDetection | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  /** The latest step image on screen, over the one before it while it crossfades in */
  const [images, setImages] = useState<{ previous?: string, live: string } | null>(null)
  /** The live image's width / height, once it has loaded */
  const [liveRatio, setLiveRatio] = useState<number>()

  const lastIdRef = useRef(scene?.id)
  const queueRef = useRef<LabScene[]>([])
  const isPlayingRef = useRef(false)
  const isAliveRef = useRef(true)
  const cameraRef = useRef<LabCamera>(LAB_CAMERA_FIT)
  const frameRequestRef = useRef(0)
  const timersRef = useRef(new Set<number>())
  const callbacksRef = useRef({ onStraighten, onImageShown })
  callbacksRef.current = { onStraighten, onImageShown }

  /** Moves the camera without a render: it changes on every frame */
  const applyCamera = useCallback((camera: LabCamera) => {
    cameraRef.current = camera
    const layer = layerRef.current
    if (!layer) return
    layer.style.transform = `translate(${camera.tx}px, ${camera.ty}px) scale(${camera.scale})`
    // Brackets and pill keep their size on screen whatever the zoom
    layer.style.setProperty('--inv', String(1 / camera.scale))
  }, [])

  const tween = useCallback((target: LabCamera, ms: number) => new Promise<void>((resolve) => {
    cancelAnimationFrame(frameRequestRef.current)
    if (prefersReducedMotion()) {
      applyCamera(LAB_CAMERA_FIT)
      resolve()
      return
    }
    const from = cameraRef.current
    const start = performance.now()
    const step = (now: number) => {
      if (!isAliveRef.current) return resolve()
      const k = easeOutCubic(Math.min(1, (now - start) / ms))
      applyCamera({
        scale: from.scale + (target.scale - from.scale) * k,
        tx: from.tx + (target.tx - from.tx) * k,
        ty: from.ty + (target.ty - from.ty) * k,
      })
      if (k < 1) frameRequestRef.current = requestAnimationFrame(step)
      else resolve()
    }
    frameRequestRef.current = requestAnimationFrame(step)
  }), [applyCamera])

  const wait = useCallback((ms: number) => new Promise<void>((resolve) => {
    const timer = window.setTimeout(() => {
      timersRef.current.delete(timer)
      resolve()
    }, ms)
    timersRef.current.add(timer)
  }), [])

  const showImage = useCallback((url: string) => {
    setImages((current) => ({ previous: current?.live, live: url }))
    callbacksRef.current.onImageShown?.(url)
  }, [])

  /** One scene, as the Playground plays it */
  const playScene = useCallback(async (next: LabScene) => {
    if (next.kind !== 'detect') {
      // Decoding while the grid plays
      const ready = preloadLabSceneImage(next.image)
      if (next.kind === 'edit' && next.steps.includes('perspective')) {
        callbacksRef.current.onStraighten?.()
        await wait(LAB_SCENE_STRAIGHTEN_MS)
      }
      await ready
      if (!isAliveRef.current) return
      showImage(next.image)
      await wait(next.kind === 'edit' ? LAB_SCENE_EDIT_MS : LAB_SCENE_GENERATE_MS)
      return
    }
    const found = await loadLabDetectionMask(next.mask).catch(() => null)
    if (!found || !isAliveRef.current) return
    setShown({ id: next.id, maskUrl: found.url, box: found.box, categories: next.categories })
    const fit = fitRef.current
    if (fit) await tween(labCameraOnBox(found.box, fit.clientWidth, fit.clientHeight), LAB_DETECTION_ZOOM_MS)
    await wait(LAB_DETECTION_HOLD_MS)
    if (isAliveRef.current) setShown(null)
  }, [showImage, tween, wait])

  const play = useCallback(async () => {
    if (isPlayingRef.current) return
    isPlayingRef.current = true
    setIsPlaying(true)
    while (queueRef.current.length > 0 && isAliveRef.current) {
      await playScene(queueRef.current.shift() as LabScene)
    }
    if (isAliveRef.current && cameraRef.current.scale !== 1) await tween(LAB_CAMERA_FIT, LAB_DETECTION_FIT_MS)
    isPlayingRef.current = false
    if (!isAliveRef.current) return
    // One may have come in while the camera returned
    if (queueRef.current.length > 0) void play()
    else setIsPlaying(false)
  }, [playScene, tween])

  useEffect(() => {
    if (!scene || scene.id === lastIdRef.current) return
    lastIdRef.current = scene.id
    queueRef.current.push(scene)
    void play()
  }, [scene, play])

  useEffect(() => {
    onPlayingChange?.(isPlaying)
  }, [isPlaying, onPlayingChange])

  useEffect(() => {
    // Alive again on StrictMode's effect replay, which runs the cleanup below and mounts again on the spot
    isAliveRef.current = true
    const timers = timersRef.current
    return () => {
      isAliveRef.current = false
      cancelAnimationFrame(frameRequestRef.current)
      timers.forEach((timer) => window.clearTimeout(timer))
    }
  }, [])

  const label = shown && (shown.categories.length > 0
    ? shown.categories.map((category) => t(`photo_studio_editing:lab.privacy.${LAB_PRIVACY_CATEGORY_KEYS[category]}`)).join(', ')
    : t('photo_studio_editing:lab.progress_step.objects'))
  const baseUrl = images?.previous ?? photoUrl
  const isActive = isPlaying || !!images

  return (
    // Laid out even when idle: the camera measures the frame as a scene starts
    <div className={styles.frame} data-scene="run" data-active={isActive || undefined} aria-hidden="true">
      <div
        ref={fitRef}
        className={styles.fit}
        data-ratio={liveRatio ? '' : undefined}
        style={liveRatio ? { '--ratio': liveRatio } as CSSProperties : undefined}
      >
        <div ref={layerRef} className={styles.layer}>

          {baseUrl && <img className={styles.image} src={baseUrl} alt="" draggable={false} />}

          {images && (
            <img
              key={images.live}
              className={`${styles.image} ${styles.crossfade}`}
              src={images.live}
              alt=""
              draggable={false}
              data-live
              onLoad={({ currentTarget }) => currentTarget.naturalHeight && setLiveRatio(currentTarget.naturalWidth / currentTarget.naturalHeight)}
            />
          )}

          <span className={styles.veil} />

          {shown && <img key={`mask-${shown.id}`} className={styles.mask} src={shown.maskUrl} alt="" draggable={false} />}

          {shown && (
            <span
              key={`box-${shown.id}`}
              className={styles.box}
              data-box
              style={{ left: `${shown.box.x * 100}%`, top: `${shown.box.y * 100}%`, width: `${shown.box.w * 100}%`, height: `${shown.box.h * 100}%` }}
            >
              <i className={`${styles.corner} ${styles.topLeft}`} />
              <i className={`${styles.corner} ${styles.topRight}`} />
              <i className={`${styles.corner} ${styles.bottomLeft}`} />
              <i className={`${styles.corner} ${styles.bottomRight}`} />
              <span className={styles.label}>{label}</span>
            </span>
          )}

        </div>
      </div>
    </div>
  )
}
